import "./refundFlow.scss";
import { CurrencyFormItem } from "@vyne-shared/components/Payment/Components/Amount/CurrencyFormItem";
import { Flex, type FormInstance, Result, Spin, Typography } from "antd";
import { HistoryTable } from "../Tabs/PatientActivity/PatientActivityTable/PatientMenu/HistoryModal/HistoryTable/HistoryTable";
import { PatientSummaryFromId } from "molecules/PatientSummary";
import {
    type StrictlyBasisPoints,
    dollarsFromBasisPoints,
    stringFromBasisPoints,
    stringFromDollars,
} from "@vyne-shared/utils/currency";
import { VyneForm } from "@vyne-shared/components/vyneForm/VyneForm";
import { getRefundAmountRemaining } from "./refundModalUtils";
import { useRefundPolling } from "./useRefundPolling";
import { useSubmitPartialRefunds } from "./useSubmitPartialRefunds";
import { validAmountNumber } from "@vyne-shared/components/Payment/Components/Amount/currencyInput";
import type { ManualPartialRefundRequest } from "api/requests";
import type { PatientHistoryResult } from "api/responses";
import type { RefundModalEvent } from "./refundModal.events";
import type { RefundResultState, TransactionRefundModalState } from "./refundModal.state";

const { Title } = Typography;

/** Properties required to support the transaction/refund workflow. */
interface RefundFlowProps {
    /** ID of the P/RP whose transaction is being refunded. */
    readonly patientId: number;

    /** Detail information about the transaction being refunded. */
    readonly transactionHistory: PatientHistoryResult;

    /** Current state of the modal. */
    readonly state: TransactionRefundModalState;

    /** Dispatch function to update the modal state. */
    readonly dispatch: React.Dispatch<RefundModalEvent>;

    /** Form instance for partial refund */
    readonly partialRefundForm: FormInstance<ManualPartialRefundRequest>;
}

// Helpers for Formatting Some String
interface PrintPaymentMethodInfoProps
    extends Readonly<React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>> {
    readonly cardBrand?: string;
    readonly cardLastFour?: string;
}

const PrintPaymentMethodInfo = ({ cardBrand, cardLastFour, ...props }: Readonly<PrintPaymentMethodInfoProps>) => (
    <span {...props}>
        {cardBrand} {cardLastFour}
    </span>
);

/** Display the various states of refund processing. */
export const RefundFlow = ({ patientId, transactionHistory, state, dispatch, partialRefundForm }: RefundFlowProps) => {
    useRefundPolling(patientId, state, dispatch);

    switch (state.type) {
        case "transaction-selected": {
            return (
                <>
                    <PatientSummaryFromId id={patientId} />
                    <TransactionRow result={transactionHistory} patientId={patientId} />
                    <TransactionSelected result={transactionHistory} partialRefundForm={partialRefundForm} />
                </>
            );
        }

        case "refund-processing":
        case "refund-polling": {
            return (
                <>
                    <PatientSummaryFromId id={patientId} />
                    <TransactionRow result={transactionHistory} patientId={patientId} />
                    <RefundProcessing />
                </>
            );
        }

        case "refund-result": {
            return <RefundResultDisplay state={state} result={transactionHistory} />;
        }
    }
};

interface PartialRefundProps {
    readonly result: PatientHistoryResult;
    readonly partialRefundForm: FormInstance<ManualPartialRefundRequest>;
}

/** Render the Transaction Selected state. */
const TransactionSelected = ({ result, partialRefundForm }: PartialRefundProps) => {
    const partialEnabled = useSubmitPartialRefunds();

    return partialEnabled ? (
        <>
            <PartialRefund result={result} partialRefundForm={partialRefundForm} />
            <PartialRefundDescription result={result} />
        </>
    ) : (
        <RefundDescription result={result} />
    );
};

interface RefundDescriptionProps {
    readonly result: PatientHistoryResult;
}

/** Display a description of the refund to be performed. */
const RefundDescription = ({ result }: RefundDescriptionProps) => {
    const fullRefundAmount = stringFromDollars(dollarsFromBasisPoints(Math.abs(result.amount ?? 0)));

    return (
        <div style={{ textAlign: "center" }}>
            <p className="refund-transaction-confirm--large">
                Are you sure you want to refund the payment of <strong>{fullRefundAmount}</strong>?
            </p>
            <p className="refund-transaction-confirm--small">
                This will be sent back to the original payment method{" "}
                <PrintPaymentMethodInfo
                    cardBrand={result.cardBrand}
                    cardLastFour={result.cardLastFour}
                    className="refund-transaction-confirm-card"
                />
                .
            </p>
        </div>
    );
};

/** Display a description of the partial refund to be performed. */
const PartialRefundDescription = ({ result }: RefundDescriptionProps) => (
    <p className="refund-transaction-confirm--partial">
        The original payment method{" "}
        <PrintPaymentMethodInfo
            cardBrand={result.cardBrand}
            cardLastFour={result.cardLastFour}
            className="refund-transaction-confirm-card"
        />{" "}
        will receive the refund amount.
    </p>
);

/** Display a form to enter a partial refund amount. */
const PartialRefund = ({ result, partialRefundForm }: PartialRefundProps) => {
    //NOTE: validAmountNumber validator requires the amount be a number, because that's what you get if the user actually
    // enters a number with CurrencyFormItem
    const remainingAmount = dollarsFromBasisPoints(getRefundAmountRemaining(result));

    return (
        <VyneForm
            className="partial-refund-input-wrapper"
            layout="vertical"
            form={partialRefundForm}
            initialValues={{ amount: remainingAmount }}
        >
            <CurrencyFormItem
                rules={[{ min: 0.01, max: remainingAmount, validator: validAmountNumber }]}
                extra={
                    <aside aria-label="Maximum refund help">Up to ${remainingAmount.toFixed(2)} can be refunded</aside>
                }
                inputProps={{
                    max: remainingAmount,
                    required: true,
                }}
            />
        </VyneForm>
    );
};

/** Render the Refund Processing state. */
const RefundProcessing = () => (
    <div className="refund-processing">
        <Title level={3}>Refund Processing</Title>
        <Spin size="large" />
    </div>
);

interface RefundResultProps {
    readonly state: RefundResultState;
    readonly result: PatientHistoryResult;
}

/** Render the Refund Result state. */
const RefundResultDisplay = ({ state, result }: RefundResultProps) => (
    <div className="refund-result-wrapper">
        <Result
            status={state.status}
            title={state.title}
            subTitle={<RefundResultDescription state={state} result={result} />}
        />
    </div>
);

const RefundResultDescription = ({ state, result }: RefundResultProps) => {
    if (state.status !== "success" && state.status !== "info") {
        return state.message;
    }

    let verb = " has been successfully sent back to card ";
    if (state.status === "info") {
        verb = " is pending for card ";
    }

    return (
        <Flex gap="0.2rem" vertical={true}>
            {state.status !== "info" && state.message !== undefined && (
                <p className="refund-state-message">{state.message}</p>
            )}
            <p className="refund-description">
                The amount of{" "}
                <span className="refund-amount">
                    {stringFromBasisPoints(Math.abs(state.amount ?? result.amount ?? 0) as StrictlyBasisPoints)}
                </span>
                {verb}
                <PrintPaymentMethodInfo
                    cardBrand={state.cardBrand}
                    cardLastFour={state.last4}
                    className="refund-amount"
                />
                .
            </p>
        </Flex>
    );
};

interface SelectedProps {
    readonly result: PatientHistoryResult;
    readonly patientId: number;
}

/** Display a single Transaction row. */
const TransactionRow = ({ result, patientId }: SelectedProps) => (
    <HistoryTable name="Transaction History Table" events={[result]} patientId={patientId} hideActions hideSorting />
);
